import { Plus, Trash2, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import type { FormQuestion, FormSchema, FormSection, QuestionType } from '@/types/careers'

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'text', label: 'Short text' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'url', label: 'URL' },
  { value: 'file', label: 'File upload' },
  { value: 'checkbox', label: 'Yes / No' },
]

function slugId(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 32) || `q_${Date.now()}`
}

interface FormBuilderProps {
  value: FormSchema
  onChange: (schema: FormSchema) => void
}

export function FormBuilder({ value, onChange }: FormBuilderProps) {
  const sections = value.sections || []

  function updateSection(idx: number, patch: Partial<FormSection>) {
    const next = sections.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    onChange({ sections: next })
  }

  function updateQuestion(sIdx: number, qIdx: number, patch: Partial<FormQuestion>) {
    const sec = sections[sIdx]
    const questions = sec.questions.map((q, i) => (i === qIdx ? { ...q, ...patch } : q))
    updateSection(sIdx, { questions })
  }

  function addSection() {
    onChange({
      sections: [
        ...sections,
        {
          id: `section_${Date.now()}`,
          title: 'New section',
          description: '',
          questions: [],
        },
      ],
    })
  }

  function removeSection(idx: number) {
    onChange({ sections: sections.filter((_, i) => i !== idx) })
  }

  function addQuestion(sIdx: number) {
    const sec = sections[sIdx]
    updateSection(sIdx, {
      questions: [
        ...sec.questions,
        {
          id: `question_${Date.now()}`,
          type: 'text',
          label: 'New question',
          required: false,
        },
      ],
    })
  }

  function removeQuestion(sIdx: number, qIdx: number) {
    const sec = sections[sIdx]
    updateSection(sIdx, { questions: sec.questions.filter((_, i) => i !== qIdx) })
  }

  return (
    <div className="space-y-6">
      {sections.map((section, sIdx) => (
        <div
          key={section.id}
          className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(6,9,12,0.4)] p-5"
        >
          <div className="flex items-start gap-3 mb-4">
            <GripVertical className="h-5 w-5 text-[#93a5ad] mt-2 shrink-0" />
            <div className="flex-1 grid gap-3">
              <Input
                value={section.title}
                onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                placeholder="Section title"
              />
              <Input
                value={section.description || ''}
                onChange={(e) => updateSection(sIdx, { description: e.target.value })}
                placeholder="Section description (optional)"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeSection(sIdx)}
              disabled={sections.length <= 1}
              aria-label="Remove section"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4 pl-8">
            {section.questions.map((q, qIdx) => (
              <div
                key={q.id}
                className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] p-4"
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    value={q.label}
                    onChange={(e) => {
                      const label = e.target.value
                      const patch: Partial<FormQuestion> = { label }
                      if (q.id.startsWith('question_') || !q.id) patch.id = slugId(label)
                      updateQuestion(sIdx, qIdx, patch)
                    }}
                    placeholder="Question label"
                  />
                  <select
                    className="h-9 rounded-[10px] border border-[rgba(255,255,255,0.1)] bg-[rgba(6,9,12,0.6)] px-3 text-sm text-[#f0f4f8]"
                    value={q.type}
                    onChange={(e) => updateQuestion(sIdx, qIdx, { type: e.target.value as QuestionType })}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                  <label className="flex items-center gap-2 text-[#9aabbb]">
                    <input
                      type="checkbox"
                      checked={!!q.required}
                      onChange={(e) => updateQuestion(sIdx, qIdx, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  {q.type === 'select' && (
                    <Input
                      className="flex-1 min-w-[200px]"
                      value={(q.options || []).join(', ')}
                      onChange={(e) =>
                        updateQuestion(
                          sIdx,
                          qIdx,
                          { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) },
                        )
                      }
                      placeholder="Options, comma-separated"
                    />
                  )}
                  {q.type === 'file' && (
                    <Input
                      className="flex-1 min-w-[200px]"
                      value={q.accept || ''}
                      onChange={(e) => updateQuestion(sIdx, qIdx, { accept: e.target.value })}
                      placeholder="Accepted files e.g. .pdf,.doc"
                    />
                  )}
                  {(q.type === 'text' || q.type === 'textarea' || q.type === 'url') && (
                    <Input
                      className="flex-1 min-w-[200px]"
                      value={q.placeholder || ''}
                      onChange={(e) => updateQuestion(sIdx, qIdx, { placeholder: e.target.value })}
                      placeholder="Placeholder text"
                    />
                  )}
                </div>

                <div className="mt-2 flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(sIdx, qIdx)}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))}

            <Button type="button" variant="secondary" size="sm" onClick={() => addQuestion(sIdx)}>
              <Plus className="h-4 w-4 mr-1" /> Add question
            </Button>
          </div>
        </div>
      ))}

      <Button type="button" variant="secondary" onClick={addSection}>
        <Plus className="h-4 w-4 mr-1" /> Add section
      </Button>
    </div>
  )
}

// This static schema is intentionally colocated with the editor it initializes.
// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_FORM_SCHEMA: FormSchema = {
  sections: [
    {
      id: 'contact',
      title: 'Contact information',
      description: 'How we can reach you about this role.',
      questions: [
        { id: 'name', type: 'text', label: 'Full name', required: true, placeholder: 'Your name' },
        { id: 'email', type: 'email', label: 'Email address', required: true, placeholder: 'you@example.com' },
      ],
    },
    {
      id: 'about',
      title: 'About you',
      description: 'Help us understand your background and interest in clyr.',
      questions: [
        {
          id: 'message',
          type: 'textarea',
          label: 'Why do you want to join clyr?',
          required: true,
          minLength: 20,
          placeholder: 'Tell us about your experience…',
        },
        {
          id: 'resume',
          type: 'file',
          label: 'Resume or CV',
          required: false,
          accept: '.pdf,.doc,.docx,.txt',
          helpText: 'PDF, Word, or plain text — max 5 MB',
        },
      ],
    },
  ],
}
